/**
 * ТЗ 0078 — шаблоны сообщений (SMS и письма) и шаблоны договоров. Словари и
 * чистые помощники.
 *
 * Модуль без React, Apollo и i18n: подписи — ключи `esign.*`, их переводит
 * вызывающий. Каждый словарь — это и список ключей, который проверяет тест
 * паритета локалей: код без перевода уронит тест, а не покажет сырой ключ.
 */

/** Каналы справочника шаблонов (`MessageTemplateType.channel`). */
export const MESSAGE_CHANNELS = ['sms', 'email'] as const;

export type MessageChannel = typeof MESSAGE_CHANNELS[number];

/**
 * Подстановки темы договора — зеркало `VARIABLES` бэкенда
 * (`esign/outreach_topic.py`). Список с сервера (`messageTemplateVariables`)
 * главнее: он решает, какие чипы показать. Этот — только для перевода
 * описаний (описание с сервера — по-русски и в польском интерфейсе).
 */
export const MESSAGE_VARIABLE_NAMES = [
  'first_name',
  'last_name',
  'number',
  'link',
  'expires',
  'seller_name',
  'course',
  'group',
  'start_date',
] as const;

/**
 * ТЗ 0084: подстановка из справочника сервера (`messageTemplateVariables`) —
 * что это, откуда берётся значение (у `{link}` — куда ведёт ссылка в этой
 * теме) и пример. Русские и польские поля оба: язык знает браузер. Поля
 * необязательны — старый ответ (только `description`) тоже читается.
 */
export interface IMessageVariableInfo {
  name: string;
  description?: string | null;
  descriptionRu?: string | null;
  descriptionPl?: string | null;
  source?: string | null;
  sourceRu?: string | null;
  sourcePl?: string | null;
  example?: string | null;
}

export interface IMessageVariableText {
  description: string;
  source: string;
  example: string;
}

const isPolish = (language: string | null | undefined): boolean =>
  (language || '').startsWith('pl');

/**
 * Описание подстановки на языке интерфейса: польское — если задано, иначе
 * русское; пустое русское — старое поле `description`.
 */
export const messageVariableText = (
  variable: IMessageVariableInfo,
  language: string | null | undefined,
): IMessageVariableText => {
  const pl = isPolish(language);
  const pick = (
    ru: string | null | undefined,
    polish: string | null | undefined,
    base?: string | null,
  ): string =>
    (pl && (polish || '').trim()) || (ru || '').trim() || (base || '').trim();
  return {
    description: pick(
      variable.descriptionRu,
      variable.descriptionPl,
      variable.description,
    ),
    source: pick(variable.sourceRu, variable.sourcePl, variable.source),
    example: (variable.example || '').trim(),
  };
};

/**
 * ТЗ 0084: где код называет шаблон (шаг лесенки, «Напомнить сейчас»,
 * письмо-приглашение) — подписи на языке интерфейса. Пусто — шаблон
 * свободен: его можно выключить и удалить.
 */
export const messageTemplateUses = (
  template: {
    usedIn?: readonly string[] | null;
    usedInPl?: readonly string[] | null;
  },
  language: string | null | undefined,
): string[] => {
  const ru = template.usedIn ?? [];
  const pl = template.usedInPl ?? [];
  return [...(isPolish(language) && pl.length ? pl : ru)];
};

/** Шаблон нужен коду — выключить и удалить нельзя (`TEMPLATE_IN_USE`). */
export const isMessageTemplateInUse = (template: {
  usedIn?: readonly string[] | null;
}): boolean => (template.usedIn?.length ?? 0) > 0;

/** `{link}` доставка добавляет всегда — его знает любая тема. */
const ALWAYS_KNOWN = ['link'];

/**
 * Та же регулярка, что у бэкенда (`PLACEHOLDER_RE`): строчные буквы и `_`.
 * Фигурная скобка в обычном тексте («{Uwaga}») подстановкой не считается.
 */
const PLACEHOLDER_SOURCE = '\\{([a-z_]+)\\}';

/** Имена подстановок в тексте — без повторов, в порядке появления. */
export const messagePlaceholders = (
  text: string | null | undefined,
): string[] => {
  // Своя регулярка на вызов: у глобальной `lastIndex` общий, и второй вызов
  // начинал бы поиск с середины строки.
  const pattern = new RegExp(PLACEHOLDER_SOURCE, 'g');
  const source = String(text ?? '');
  const found: string[] = [];
  let match = pattern.exec(source);
  while (match !== null) {
    if (!found.includes(match[1])) found.push(match[1]);
    match = pattern.exec(source);
  }
  return found;
};

/**
 * Подстановки, которых тема не знает. Бэкенд откажет с `UNKNOWN_PLACEHOLDER`
 * и назовёт их по-русски; экран называет их сам — в польском интерфейсе
 * текст сервера был бы русским.
 */
export const messageUnknownPlaceholders = (
  text: string | null | undefined,
  known: readonly string[],
): string[] => {
  const allowed = new Set([...known, ...ALWAYS_KNOWN]);
  return messagePlaceholders(text)
    .filter((name) => !allowed.has(name))
    .sort();
};

/**
 * Перепроверка 0084: `{link}` в теме, где ссылки нет (в справочнике
 * подстановок темы её нет, — «Напоминания» лида). Сервер откажет
 * `NO_LINK_HERE`; экран говорит сразу. Пока справочник не пришёл — не
 * судим.
 */
export const messageLinkNotInTopic = (
  text: string | null | undefined,
  known: readonly string[],
): boolean =>
  known.length > 0 &&
  !known.includes('link') &&
  messagePlaceholders(text).includes('link');

/**
 * В тексте есть `{link}`. У подписанного договора ссылки на подпись больше
 * нет — такой текст сервер не отправит (`LINK_NOT_READY`).
 */
export const messageHasLink = (text: string | null | undefined): boolean =>
  messagePlaceholders(text).includes('link');

/**
 * Вставить `{name}` на место курсора (или вместо выделения). Возвращает
 * новый текст и позицию курсора сразу за вставкой — чтобы следующий чип
 * встал после этого, а не в начало поля.
 */
export const insertPlaceholder = (
  value: string,
  selectionStart: number | null | undefined,
  selectionEnd: number | null | undefined,
  name: string,
): { value: string; caret: number } => {
  const token = `{${name}}`;
  const { length } = value;
  const clamp = (position: number | null | undefined, fallback: number) =>
    typeof position === 'number' && position >= 0 && position <= length
      ? position
      : fallback;
  const start = clamp(selectionStart, length);
  const end = Math.max(start, clamp(selectionEnd, start));
  return {
    value: value.slice(0, start) + token + value.slice(end),
    caret: start + token.length,
  };
};

/**
 * Коды отказа отправки SMS со своим текстом (`sendContractSms`) и причины
 * `previewContractSms.reason` — переводятся `esign.sms.errors.<CODE>`.
 */
export const SMS_ERROR_CODES = [
  'NOT_FOUND',
  'SMS_DISABLED',
  'NO_PHONE',
  'WRONG_STATUS',
  'COOLDOWN',
  'OPTED_OUT',
  'STOPPED',
  'DAILY_CAP',
  'FOREIGN_NUMBER',
  'UNKNOWN_PLACEHOLDER',
  'TOO_LONG',
  'REQUIRED',
  'NO_ACCESS',
  'NETWORK',
  // ТЗ 0084: ссылка в тексте уходит только короткой — сократителя нет, SMS
  // не уйдёт.
  'SHORTLINK_DISABLED',
  // Договор уже подписан: SMS подписанту можно, но без `{link}` — ссылки на
  // подпись больше нет.
  'LINK_NOT_READY',
] as const;

/** Кодировки SMS: `gsm7` — 160 знаков в сегменте, `ucs2` — 70. */
export const SMS_ENCODINGS = ['gsm7', 'ucs2'] as const;

/**
 * Коды отказа справочника шаблонов (`saveMessageTemplate`,
 * `deleteMessageTemplate`) и письма со своим текстом — переводятся
 * `esign.templates.errors.<CODE>`.
 */
export const MESSAGE_TEMPLATE_ERROR_CODES = [
  'UNKNOWN_PLACEHOLDER',
  'TOO_LONG',
  'REQUIRED',
  'INVALID',
  'NOT_FOUND',
  'SYSTEM_TEMPLATE',
  'LINK_NOT_READY',
  'LINK_IN_SUBJECT',
  // ТЗ 0084: шаблон выбран у шага лесенки, у «Напомнить сейчас» или это
  // письмо-приглашение — выключить и удалить нельзя; `{link}` в теме, где
  // ссылки нет («Напоминания» лида).
  'TEMPLATE_IN_USE',
  'NO_LINK_HERE',
  'FORBIDDEN',
  // Перепроверка 0084: используемый шаблон темы со ссылкой (шаг лесенки,
  // приглашение, оплата, LMS, «Анкета») без `{link}` не сохранить.
  'LINK_REQUIRED',
  // ТЗ 0094: личную версию этого шаблона завести нельзя; два «Сохранить»
  // подряд создали одну и ту же личную копию.
  'PERSONAL_NOT_ALLOWED',
  'CONFLICT',
] as const;

/**
 * Подстановки, запрещённые в ТЕМЕ письма: ссылка на подпись в теме видна
 * в списке писем и в уведомлениях — бэкенд отказывает `LINK_IN_SUBJECT`.
 */
export const MESSAGE_SUBJECT_FORBIDDEN = ['link'] as const;

/** Есть ли в теме письма запрещённая подстановка (`{link}`). */
export const messageSubjectHasLink = (
  subject: string | null | undefined,
): boolean =>
  messagePlaceholders(subject).some((name) =>
    (MESSAGE_SUBJECT_FORBIDDEN as readonly string[]).includes(name),
  );

/* ── Личные шаблоны продавцов (ТЗ 0094) ─────────────────────────────── */

/**
 * Темы, где у сотрудника бывают личные шаблоны — зеркало
 * `Topic.personal_templates` бэкенда: подпись договора и лиды.
 */
export const MESSAGE_PERSONAL_TOPICS = ['esign_signature', 'lead_sms'] as const;

/**
 * Коды, у которых личная версия ЗАМЕНЯЕТ общий текст в отправках владельца
 * (`esign/outreach_topic.PERSONAL_CODES`): письма-приглашения, первое SMS
 * лесенки, «Напомнить сейчас». Остальные шаги лесенок — только общие.
 */
export const MESSAGE_PERSONAL_CODES = [
  'invite',
  'invite_company',
  'invite_teacher_framework',
  'invite_teacher_annex',
  'esign_initial',
  'esign_manual',
] as const;

interface IPersonalTemplateLike {
  code: string;
  isSystem?: boolean | null;
  isPersonal?: boolean | null;
  usedIn?: readonly string[] | null;
}

/** В теме бывают личные шаблоны. */
export const topicAllowsPersonal = (
  topic: string | null | undefined,
): boolean =>
  (MESSAGE_PERSONAL_TOPICS as readonly string[]).includes(
    topic || 'esign_signature',
  );

/**
 * Можно ли сделать свою версию общего шаблона — то же правило, что у
 * бэкенда (`personal_refusal`), иначе он откажет `PERSONAL_NOT_ALLOWED`:
 * тема с личными шаблонами и либо код из `MESSAGE_PERSONAL_CODES`, либо
 * ручная заготовка (не системная и коду не нужна).
 */
export const canMakePersonalVersion = (
  template: IPersonalTemplateLike,
  topic: string | null | undefined,
): boolean => {
  if (template.isPersonal || !topicAllowsPersonal(topic)) return false;
  if ((MESSAGE_PERSONAL_CODES as readonly string[]).includes(template.code)) {
    return true;
  }
  return !template.isSystem && (template.usedIn?.length ?? 0) === 0;
};

/**
 * Свои личные шаблоны — первыми (бэкенд уже так отдаёт; порядок
 * закрепляем, чтобы выбор не зависел от него). Чужие личные (Admin c
 * `includeOthers`) в выбор для отправки не попадают — бэкенд их не шлёт.
 */
export const pickableTemplates = <
  T extends { isPersonal?: boolean | null; canEdit?: boolean | null }
>(
  templates: readonly T[],
): T[] => {
  const own = templates.filter((item) => item.isPersonal && item.canEdit);
  const common = templates.filter((item) => !item.isPersonal);
  return [...own, ...common];
};

/**
 * ТЗ 0094: окно отправки договора — почему письмо не уйдёт
 * (`contractInvitePreview.skipReason`), переводится
 * `esign.invite.skip.<reason>`.
 */
export const ESIGN_INVITE_SKIP_REASONS = [
  'wrong_status',
  'mail_disabled',
  'shortlink_disabled',
  'no_mailbox',
  'no_template',
] as const;

/**
 * ТЗ 0094: отказы разовой правки письма (`sendContract`,
 * `contractInvitePreview.errorCode`) — переводятся `esign.invite.errors.<CODE>`.
 */
export const ESIGN_INVITE_ERROR_CODES = [
  'LINK_REQUIRED',
  'UNKNOWN_PLACEHOLDER',
  'LINK_IN_SUBJECT',
  'EMAIL_NOT_REQUESTED',
  'EMAIL_EDIT_UNAVAILABLE',
  'TOO_LONG',
  'REQUIRED',
] as const;

/** ERP #133: отказ по выбранному шаблону письма (поле `templateId`). */
export const ESIGN_INVITE_TEMPLATE_ERROR_CODES = [
  'NOT_FOUND',
  'TEMPLATE_FORBIDDEN',
] as const;

/* ── Шаблоны договоров ────────────────────────────────────────────────── */

/** `ContractTemplateType.appendix`. */
export const ESIGN_TEMPLATE_APPENDICES = [
  'none',
  'group_change',
  'dofinansowanie',
] as const;

export const ESIGN_APPENDIX_DOFIN = 'dofinansowanie';

const ESIGN_APPENDIX_GROUP_CHANGE = 'group_change';

interface ITemplateLike {
  id: string;
  appendix: string;
  isActive?: boolean | null;
}

interface IPlanLike {
  marksStatePrefinanced?: boolean | null;
}

/**
 * Тариф с дофинансированием требует шаблон с таким приложением, и наоборот.
 *
 * 🔴 Правило — ровно как у бэкенда (`drafts.student_template`): решает флаг
 * тарифа `marksStatePrefinanced`, а не его код. Вторая, «чуть умнее»,
 * копия правила разошлась бы с сервером, и форма предлагала бы шаблон, от
 * которого сервер откажется (`TEMPLATE_PLAN_MISMATCH`).
 */
export const esignTemplateFitsPlan = (
  template: Pick<ITemplateLike, 'appendix'>,
  plan: IPlanLike | null | undefined,
): boolean =>
  (template.appendix === ESIGN_APPENDIX_DOFIN) ===
  Boolean(plan?.marksStatePrefinanced);

/**
 * Коды шаблонов договоров, у которых есть название и описание на языке
 * интерфейса (`esign.templates.titles|descriptions.<code>`). Имя шаблона в
 * базе — польское юридическое название («Application do umowy…»): оно
 * остаётся второй строкой, а продажник выбирает по понятному «Стандартный /
 * С дофинансированием». Новый код без перевода показывается именем из базы.
 */
export const ESIGN_TEMPLATE_CODES = [
  'student_application',
  'student_application_dofin',
  'company_b2b',
  'teacher_framework_zlecenie',
  'teacher_framework_b2b',
  'teacher_annex',
] as const;

const isKnownTemplateCode = (code: string): boolean =>
  (ESIGN_TEMPLATE_CODES as ReadonlyArray<string>).includes(code);

/** Ключ названия шаблона или `null` — тогда берётся имя из базы. */
export const esignTemplateTitleKey = (code: string): string | null =>
  isKnownTemplateCode(code) ? `esign.templates.titles.${code}` : null;

/** Ключ описания шаблона или `null` — тогда описание из базы. */
export const esignTemplateDescriptionKey = (code: string): string | null =>
  isKnownTemplateCode(code) ? `esign.templates.descriptions.${code}` : null;

/**
 * Шаблон по умолчанию для тарифа: дофинансирование → приложение
 * «дофинансирование», иначе → «смена группы»; нет такого — любой
 * включённый, подходящий к тарифу. Список бэкенд отдаёт старшей версией
 * вперёд, поэтому первый найденный — актуальный.
 *
 * `null` — подходящего нет: тогда `templateId` не уходит вовсе, и шаблон
 * выбирает сервер (или честно отказывает `TEMPLATE_UNAVAILABLE`).
 */
export const esignDefaultTemplateId = (
  templates: readonly ITemplateLike[],
  plan: IPlanLike | null | undefined,
): string | null => {
  const active = templates.filter((item) => item.isActive !== false);
  const wanted = plan?.marksStatePrefinanced
    ? ESIGN_APPENDIX_DOFIN
    : ESIGN_APPENDIX_GROUP_CHANGE;
  const exact = active.find((item) => item.appendix === wanted);
  if (exact) return exact.id;
  const fitting = active.find((item) => esignTemplateFitsPlan(item, plan));
  return fitting ? fitting.id : null;
};
